import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, Download } from 'lucide-react';

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
};

export default function OilAdminAudit({ seasonId }) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [skip, setSkip] = useState(0);
  const [f, setF] = useState({ action: '', kako_id: '', date_from: '', date_to: '' });

  const params = useCallback(() => (
    Object.fromEntries(Object.entries(f).filter(([, v]) => v))
  ), [f]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/oil/season/${seasonId}/audit-log`,
        { params: { skip, limit: 30, ...params() } });
      setRows(r.data.audit || []);
      setTotal(r.data.total || 0);
      setActions(r.data.actions || []);
    } catch { toast.error('Erro ao carregar auditoria.'); }
  }, [seasonId, skip, params]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setSkip(0); };

  const exportCsv = async () => {
    try {
      const r = await api.get(`/admin/oil/season/${seasonId}/audit-log.csv`,
        { params: params(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `oil-auditoria-${seasonId.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao exportar CSV.'); }
  };

  return (
    <div className="space-y-3" data-testid="oil-admin-audit">
      <div className="flex flex-wrap items-center gap-2">
        <select value={f.action} onChange={(e) => set('action', e.target.value)} data-testid="oil-audit-filter-action"
          className="h-9 rounded-lg border bg-white px-2 text-xs">
          <option value="">Todas as ações</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input value={f.kako_id} onChange={(e) => set('kako_id', e.target.value)} placeholder="KAKO ID"
          data-testid="oil-audit-filter-kako" className="h-9 w-32 rounded-lg border px-2 text-xs" />
        <input type="date" value={f.date_from} onChange={(e) => set('date_from', e.target.value)}
          data-testid="oil-audit-filter-from" className="h-9 rounded-lg border px-2 text-xs" />
        <input type="date" value={f.date_to} onChange={(e) => set('date_to', e.target.value)}
          data-testid="oil-audit-filter-to" className="h-9 rounded-lg border px-2 text-xs" />
        <span className="text-xs text-slate-500" data-testid="oil-audit-total">{total} registros</span>
        <Button size="sm" variant="outline" onClick={exportCsv} data-testid="oil-audit-export">
          <Download className="mr-1 h-3.5 w-3.5" /> CSV
        </Button>
      </div>

      {!rows ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div> : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr><th className="p-2 text-left">Data</th><th className="p-2 text-left">Admin</th>
                <th className="p-2 text-left">Ação</th><th className="p-2 text-left">Sobre</th>
                <th className="p-2 text-left">Detalhes</th></tr>
            </thead>
            <tbody>
              {rows.map((a, i) => (
                <tr key={i} className="border-t align-top" data-testid={`oil-audit-row-${i}`}>
                  <td className="p-2 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                  <td className="p-2">{a.admin_name || '—'}</td>
                  <td className="p-2 font-bold">{a.action}</td>
                  <td className="p-2">{a.kako_id || '—'}</td>
                  <td className="p-2 font-mono text-[10px] text-slate-500">
                    {JSON.stringify(a.details || {}).slice(0, 240)}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="p-3 text-center text-slate-400">Nenhum registro com esses filtros.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={skip === 0} data-testid="oil-audit-prev"
          onClick={() => setSkip(Math.max(0, skip - 30))}>Anterior</Button>
        <Button size="sm" variant="outline" disabled={skip + 30 >= total} data-testid="oil-audit-next"
          onClick={() => setSkip(skip + 30)}>Próximos</Button>
      </div>
    </div>
  );
}
